import React, { useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useDAW } from "./DAWProvider";

const AUDIO_ACCEPT = ".wav,.mp3,.ogg,.m4a,.aac,.flac,.webm";

function PadRow({ padIndex, pad }) {
  const {
    pattern,
    toggleStep,
    currentStep,
    isPlaying,
    totalSteps,
    mutedRows,
    toggleRowMute,
    triggerPad,
    customPadNames,
    customPadFlags,
    setPadName,
    importPadSound,
    resetPad,
    sessionRole,
    canEditSequencer,
    canUploadRecord,
    canDelete,
  } = useDAW();

  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const stepsPerBar = 16;

  const displayName = customPadNames[padIndex] || pad.name;
  const isCustom = customPadFlags[padIndex];

  const handleDoubleClick = (e) => {
    e.preventDefault();
    setEditValue(displayName);
    setIsEditing(true);
  };

  const handleNameSubmit = () => {
    if (editValue.trim()) {
      setPadName(padIndex, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await importPadSound(padIndex, file);
    }
    e.target.value = "";
  };

  return (
    <div
      className={`daw-sequencer__row${mutedRows[padIndex] ? " daw-sequencer__row--muted" : ""}`}
    >
      {/* Row label */}
      <div className="daw-sequencer__row-label">
        {isEditing ? (
          <input
            className="daw-sequencer__name-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleNameSubmit();
              if (e.key === "Escape") setIsEditing(false);
            }}
            autoFocus
          />
        ) : (
          <button
            className={`daw-sequencer__sound-name${isCustom ? " daw-sequencer__sound-name--custom" : ""}`}
            style={{ color: pad.color }}
            onPointerDown={() => triggerPad(padIndex)}
            onDoubleClick={handleDoubleClick}
            title={`Click: preview | Double-click: rename`}
          >
            {displayName}
          </button>
        )}
        <div className="daw-sequencer__row-actions">
          {canUploadRecord && (
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ color: "#666", padding: "2px", "&:hover": { color: "#EE772F" } }}
              title="Upload custom sound"
            >
              <FileUploadIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
          {isCustom && canDelete && (
            <IconButton
              size="small"
              onClick={() => resetPad(padIndex)}
              sx={{ color: "#666", padding: "2px", "&:hover": { color: "#e53935" } }}
              title="Reset to default sound"
            >
              <RestartAltIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
          {(!sessionRole || sessionRole === "host") && (
            <IconButton
              size="small"
              onClick={() => toggleRowMute(padIndex)}
              sx={{ color: mutedRows[padIndex] ? "#e53935" : "#555", padding: "2px" }}
              title={mutedRows[padIndex] ? "Unmute" : "Mute"}
            >
              {mutedRows[padIndex]
                ? <VolumeOffIcon sx={{ fontSize: 14 }} />
                : <VolumeUpIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={AUDIO_ACCEPT}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Step cells */}
      {Array.from({ length: totalSteps }, (_, step) => {
        const isOn = pattern[padIndex]?.[step];
        const isCurrentStep = step === currentStep && isPlaying;
        const beatInBar = step % stepsPerBar;
        const isOddGroup = Math.floor(beatInBar / 4) % 2 === 1;

        return (
          <button
            key={step}
            className={
              "daw-sequencer__cell" +
              (isOn ? " daw-sequencer__cell--on" : "") +
              (isCurrentStep ? " daw-sequencer__cell--current" : "") +
              (isOddGroup ? " daw-sequencer__cell--odd-group" : "")
            }
            style={isOn ? { "--cell-color": pad.color } : undefined}
            onClick={canEditSequencer ? () => toggleStep(padIndex, step) : undefined}
            disabled={!canEditSequencer}
          />
        );
      })}
    </div>
  );
}

export default function StepSequencer() {
  const { kit, totalSteps, currentStep, isPlaying, numBars } = useDAW();

  const stepsPerBar = 16;

  return (
    <div className="daw-sequencer">
      {/* Step number header */}
      <div className="daw-sequencer__header">
        <div className="daw-sequencer__row-label" />
        {Array.from({ length: totalSteps }, (_, step) => {
          const barIndex = Math.floor(step / stepsPerBar);
          const beatInBar = step % stepsPerBar;
          const isBeat = beatInBar % 4 === 0;
          return (
            <div
              key={step}
              className={`daw-sequencer__step-num${isBeat ? " daw-sequencer__step-num--beat" : ""}${step === currentStep && isPlaying ? " daw-sequencer__step-num--active" : ""}`}
            >
              {isBeat ? Math.floor(beatInBar / 4) + 1 + barIndex * 4 : ""}
            </div>
          );
        })}
      </div>

      {/* Sound rows */}
      {kit.pads.map((pad, padIndex) => (
        <PadRow key={padIndex} padIndex={padIndex} pad={pad} />
      ))}
    </div>
  );
}
